import "./ProjectDetailsAccordion.css";
import "../../Products.css";
import axios from "axios";
import parse from "html-react-parser";
import { useState, useEffect, useCallback } from "react";
import { Alert, Button, Accordion, Container, Row, Col } from "react-bootstrap";

import { AUTH_ROLES } from "api/common/types/auth.types";
import { useProductUpdateMutation } from "api/memberPortal/project/product/product.api";
import { PRODUCT_STATUSES } from "api/memberPortal/project/product/productStatuses.type";
import ECRCommentsTable from "components/ECR/ECRCommentsTable";
import TestedFeature from "components/TestedFeature/TestedFeature";
import TechnicalQueriesTable from "components/TRRTS/TRRTSManager/TechnicalQueriesTable";
import HTMLEditor from "components/UI/HTMLEditor";
import { useAuth } from "hooks/useAuth";
import { useInterval } from "hooks/useInterval";

import AMHistoryTable from "./AMHistoryTable";
import EcrCommentsCount from "./EcrCommentsCount.jsx";
import ProjectConfirmSaveModal from "./ProjectConfirmSaveModal.jsx";
import EARDetailsAccordion from "features/memberPortal/EAR/components/EarAccordion/EarDetailsAccordion";

export const PROJECT_DESCRIPTION_FIELDS = {
  product_description: "Product Description",
  security_evaluation_summary: "Security Evaluation Summary",
  environmental_strengths: "Environmental Strengths",
  evaluation_configuration: "Evaluation Configuration",
};

export default function ProjectDetailsAccordion({
  id,
  project,
  refetch,
  assignedOrg,
}) {
  const EAR_ACCORDION = 5;
  const [activeKey, setActiveKey] = useState([EAR_ACCORDION]); // This is the key of the EAR Accordion. We want the page to start with it open
  const [editedHtmlFields, setEditedHtmlFields] = useState({});
  const [showAlert, setShowAlert] = useState("");
  const [ecrCounts, setEcrCounts] = useState({});
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const { authToken, permissions } = useAuth();
  const [failedToSave, setFailedToSave] = useState([]);
  const [triggerProductUpdate] = useProductUpdateMutation();

  const projectDescriptionFields = [
    {
      title: PROJECT_DESCRIPTION_FIELDS.product_description,
      name: "product_description",
      text: project?.product_description,
    },
    {
      title: PROJECT_DESCRIPTION_FIELDS.security_evaluation_summary,
      name: "security_evaluation_summary",
      text: project?.security_evaluation_summary,
    },
    {
      title: PROJECT_DESCRIPTION_FIELDS.environmental_strengths,
      name: "environmental_strengths",
      text: project?.environmental_strengths,
    },
    {
      title: PROJECT_DESCRIPTION_FIELDS.evaluation_configuration,
      name: "evaluation_configuration",
      text: project?.evaluation_configuration,
    },
    {
      title: "Tested Features",
    },
    { title: "EAR Review" },
    { title: "Assurance Maintenance History" },
    { title: "Technical Queries" },
    { title: "ECR Comments" },
  ];

  const expandAll = () => {
    const allKeys = [];
    projectDescriptionFields.forEach((pd, idx) => {
      allKeys.push(idx);
    });
    setActiveKey(allKeys);
  };

  const collapseAll = () => {
    setActiveKey([]);
  };

  const handleItemClick = (idx) => {
    let newKeys = [...activeKey];
    if (newKeys.includes(idx)) {
      newKeys = newKeys.filter((key) => key !== idx);
    } else {
      newKeys.push(idx);
    }
    setActiveKey(newKeys);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const startingValue = project[name];

    // HACK:
    // This is essentially replicating functionality that Tanstack Form  will handle once we switch over
    // This is basically doing a hand rolled dirty/touched check
    // This will prevent the edited alert from firing on the initial render
    if (startingValue === value) return;

    setEditedHtmlFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchEcrCounts = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}ecr/count_comments?vid=${project.v_id}`,
        {
          withCredentials: true,
          headers: { Authorization: `Token ${authToken}` },
        },
      )
      .then((response) => {
        setEcrCounts(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [project]);

  useEffect(() => {
    fetchEcrCounts();
  }, [fetchEcrCounts]);

  useInterval(() => {
    fetchEcrCounts();
  }, 180000); //3 min

  const isNiap = permissions?.role_type === AUTH_ROLES.NIAP;
  const isLab = permissions.role_type === AUTH_ROLES.LAB;
  const isValidator = permissions.role_type === AUTH_ROLES.VALIDATOR;
  const projectIsCertified = project.status === PRODUCT_STATUSES.CERTIFIED;
  const projectInAm =
    project.status === PRODUCT_STATUSES.IN_ASSURANCE_MAINTENANCE;
  const projectIsSigned = !!project.signature;
  const isLabAndSignedOrIsValidator =
    (isLab && projectIsSigned) || isValidator;

  const handleSaveAll = async () => {
    try {
      await triggerProductUpdate({
        product_id: project.product_id,
        ...editedHtmlFields,
      }).unwrap();
      setEditedHtmlFields({});
      setShowAlert("success");
      setShowConfirmSave(false);
      refetch();
    } catch (error) {
      setShowAlert("fail");
      setShowConfirmSave(false);
      const errData = error?.data;
      const message = [];
      Object.keys(errData).forEach((key) => {
        if (PROJECT_DESCRIPTION_FIELDS[key]) {
          message.push(`${PROJECT_DESCRIPTION_FIELDS[key]}: ${errData[key]}`);
        } else {
          message.push(`${key}: ${errData[key]}`);
        }
      });
      setFailedToSave(message);
    }
  };

  return (
    <Container className="padding-xs-0">
      <ProjectConfirmSaveModal
        show={showConfirmSave}
        handleClose={() => setShowConfirmSave(false)}
        handleSave={handleSaveAll}
        vid={project.v_id}
        editedHTMLChanges={editedHtmlFields}
      />
      <Row>
        <Col>
          {Object.keys(editedHtmlFields).length > 0 && showAlert === "" && (
            <Alert variant="warning">Please Save Changes!</Alert>
          )}
          {showAlert === "success" && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setShowAlert("")}
            >
              Changes successfully saved!
            </Alert>
          )}
          {showAlert === "fail" && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => {
                setShowAlert("");
                setFailedToSave([]);
              }}
            >
              Changes failed to save!
              {failedToSave.length > 0 && (
                <ul>
                  {failedToSave.map((msg) => (
                    <li key={`failedmsg-${msg.split(":")[0]}`}>{msg}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          {activeKey.length !== projectDescriptionFields.length && (
            <Button
              variant="link"
              onClick={expandAll}
              className="text-primary ml-auto"
            >
              Expand All
            </Button>
          )}
          {activeKey.length === projectDescriptionFields.length && (
            <Button
              variant="link"
              onClick={collapseAll}
              className="text-primary ml-auto"
            >
              Collapse All
            </Button>
          )}
        </Col>
        <Col className="d-flex justify-content-end mb-2">
          {Object.keys(editedHtmlFields).length > 0 && (
            <Button
              variant="success"
              onClick={() =>
                project.status === "Certified" ||
                project.status === "In Assurance Maintenance"
                  ? setShowConfirmSave(true)
                  : handleSaveAll()
              }
            >
              Save All
            </Button>
          )}
        </Col>
      </Row>
      <Accordion activeKey={activeKey} alwaysOpen flush>
        {projectDescriptionFields.map((pd, idx) => (
          <Accordion.Item
            eventKey={idx}
            key={idx}
            className={pd.title !== "ECR Comments" ? "border-bottom-dark" : ""}
          >
            <Accordion.Header onClick={() => handleItemClick(idx)}>
              <Container fluid>
                <Row>
                  <Col xs={12} sm={3}>
                    <h6 className="mb-2 mb-lg-0">{pd.title}</h6>
                  </Col>
                  {pd.title === "ECR Comments" && (
                    <Col xs={12} sm={9} className="mt-xs-2">
                      <div className="d-flex flex-wrap justify-content-start">
                        {permissions?.role_type !== "Lab" && (
                          <EcrCommentsCount ecrCounts={ecrCounts} />
                        )}
                        {/* <span className='border p-2 me-2 mb-2'>Total: {ecrCounts?.total}</span> */}
                      </div>
                    </Col>
                  )}
                </Row>
              </Container>
            </Accordion.Header>
            <Accordion.Body className="padding-xs-0">
              <>
                {pd.title === "ECR Comments" && project.v_id && (
                  <ECRCommentsTable
                    vidNumber={project.v_id}
                    fetchEcrCounts={fetchEcrCounts}
                  />
                )}
                {pd.title === "EAR Review" && (
                  <EARDetailsAccordion id={id} defaultActiveKey="0" />
                )}
                {pd.title === "Assurance Maintenance History" && (
                  <AMHistoryTable
                    id={id}
                    product={project}
                    fetchProject={refetch}
                  />
                )}
                {pd.title === "Tested Features" && project.product_id && (
                  <TestedFeature
                    featureType="product"
                    featureId={project.product_id}
                    readOnly={isLabAndSignedOrIsValidator}
                  />
                )}
                {pd.title === "Technical Queries" ? (
                  <TechnicalQueriesTable id={id} />
                ) : (
                  <>
                    {((isNiap && (projectIsCertified || projectInAm)) ||
                      (!projectIsCertified && !projectInAm && assignedOrg)) &&
                    ![
                      "ECR Comments",
                      "EAR Review",
                      "Assurance Maintenance History",
                      "Technical Queries",
                      "Tested Features",
                    ]?.includes(pd.title) ? (
                      <HTMLEditor
                        name={pd.name}
                        handleChange={handleChange}
                        setIsValid={() => {}}
                        isValid={true}
                        defaultValue={pd.text}
                      />
                    ) : (
                      pd.text && parse(pd.text)
                    )}
                  </>
                )}
              </>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </Container>
  );
}
